import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Upload,
  Modal,
  Form,
  Select,
  Input,
  Tag,
  Space,
  Typography,
  message,
  Image,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileExcelOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { documentsService } from '../services/documentsService';
import { useResponsive } from '../hooks/useResponsive';
import { RBACService, PERMISSIONS } from '../utils/rbac';
import './Documents.css';

const { Title } = Typography;
const { Option } = Select;

interface Document {
  id: number;
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  document_type: string;
  reference_type: string;
  reference_id: number;
  uploaded_by: string;
  created_at: string;
}

const Documents: React.FC = () => {
  const { isMobile } = useResponsive();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewImageLoading, setPreviewImageLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [allDocs, setAllDocs] = useState<Document[]>([]);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const canReadDocuments = RBACService.hasPermission(PERMISSIONS.DOCUMENT_READ);
  const canUploadDocuments = RBACService.hasPermission(PERMISSIONS.DOCUMENT_UPLOAD);
  const canDeleteDocuments = RBACService.hasPermission(PERMISSIONS.DOCUMENT_DELETE);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await documentsService.getDocuments();
      setAllDocs(data);
      setDocuments(data);
    } catch (error) {
      message.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (values: any) => {
    try {
      const formData = new FormData();
      formData.append('file', values.file.file);
      formData.append('document_type', values.document_type);
      formData.append('reference_type', values.reference_type);
      formData.append('reference_id', values.reference_id);

      await documentsService.uploadDocument(formData);
      message.success('Document uploaded successfully');
      setUploadModalVisible(false);
      form.resetFields();
      loadDocuments();
    } catch (error) {
      message.error('Failed to upload document');
    }
  };

  const handleDownload = async (documentId: number, filename: string) => {
    try {
      const blob = await documentsService.downloadDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('Document downloaded successfully');
    } catch (error) {
      message.error('Failed to download document');
    }
  };

  const fileKind = (doc: Document) => {
    const mime = (doc.mime_type || '').toLowerCase();
    const name = (doc.original_filename || '').toLowerCase();
    if (mime.includes('image') || /\.(jpe?g|png|gif|webp)$/.test(name)) return 'image';
    if (mime.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
    return 'other';
  };

  const openPreview = async (doc: Document) => {
    setSelectedDocument(doc);
    setPreviewModalVisible(true);
    setPreviewImageUrl(null);
    setPreviewError(null);
    const kind = fileKind(doc);
    if (kind === 'other') return;
    try {
      setPreviewImageLoading(true);
      const blob = await documentsService.downloadDocument(doc.id);
      const typed = kind === 'pdf' && blob.type !== 'application/pdf'
        ? new Blob([blob], { type: 'application/pdf' })
        : blob;
      setPreviewImageUrl(window.URL.createObjectURL(typed));
    } catch {
      setPreviewError('Could not open this file. Use Download if it is still on the server.');
      message.error('Failed to load preview');
    } finally {
      setPreviewImageLoading(false);
    }
  };

  const closePreview = () => {
    if (previewImageUrl) {
      window.URL.revokeObjectURL(previewImageUrl);
    }
    setPreviewImageUrl(null);
    setPreviewError(null);
    setPreviewModalVisible(false);
    setSelectedDocument(null);
  };

  const handleDelete = async (documentId: number) => {
    try {
      await documentsService.deleteDocument(documentId);
      message.success('Document deleted successfully');
      loadDocuments();
    } catch (error) {
      message.error('Failed to delete document');
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return <FilePdfOutlined style={{ color: '#f40' }} />;
    if (mimeType.includes('image')) return <FileImageOutlined style={{ color: '#52c41a' }} />;
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) 
      return <FileExcelOutlined style={{ color: '#52c41a' }} />;
    return <FileTextOutlined />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const columns = [
    {
      title: 'File',
      dataIndex: 'original_filename',
      key: 'original_filename',
      render: (filename: string, record: Document) => (
        <Space>
          {getFileIcon(record.mime_type)}
          <span>{filename}</span>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'document_type',
      key: 'document_type',
      render: (type: string) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: 'Reference',
      key: 'reference',
      render: (record: Document) => (
        <div>
          <div><Tag color="green">{record.reference_type}</Tag></div>
          <div style={{ fontSize: '12px', color: '#666' }}>ID: {record.reference_id}</div>
        </div>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'file_size',
      key: 'file_size',
      render: (size: number) => formatFileSize(size),
    },
    {
      title: 'Uploaded By',
      dataIndex: 'uploaded_by',
      key: 'uploaded_by',
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      align: 'center' as const,
      render: (record: Document) => (
        <Space wrap={false} size={8} style={{ display: 'inline-flex' }}>
          <Tooltip title="Preview" mouseEnterDelay={0.2}>
            <Button aria-label="Preview" className="icon-btn icon-btn--primary" shape="round" icon={<EyeOutlined />} onClick={() => openPreview(record)} />
          </Tooltip>
          {canReadDocuments && (
            <Tooltip title="Download" mouseEnterDelay={0.2}>
              <Button aria-label="Download" className="icon-btn icon-btn--info" shape="round" icon={<DownloadOutlined />} onClick={() => handleDownload(record.id, record.original_filename)} />
            </Tooltip>
          )}
          {canDeleteDocuments && (
            <Popconfirm title="Delete this document?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
              <Tooltip title="Delete" mouseEnterDelay={0.2}>
                <Button aria-label="Delete" className="icon-btn icon-btn--danger" shape="round" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="documents-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <Title level={2} style={{ margin: 0 }}>Documents</Title>
        <Space style={{ width: isMobile ? '100%' : 'auto', marginLeft: isMobile ? 0 : 'auto' }} wrap direction={isMobile ? 'vertical' : 'horizontal'}>
          <Input
            className="app-search"
            placeholder="Search documents..."
            allowClear
            value={searchText}
            onChange={(e) => {
              const q = e.target.value;
              setSearchText(q);
              const val = (q || '').toLowerCase();
              const filtered = allDocs.filter(d =>
                d.original_filename.toLowerCase().includes(val) ||
                String(d.reference_id).includes(val) ||
                d.reference_type.toLowerCase().includes(val)
              );
              setDocuments(filtered);
            }}
            style={{ width: isMobile ? '100%' : 220, minWidth: isMobile ? undefined : 220 }}
            prefix={<SearchOutlined />}
          />
          {canUploadDocuments && (
            <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadModalVisible(true)} style={isMobile ? { width: '100%' } : undefined}>Upload Document</Button>
          )}
        </Space>
      </div>

      <Card className="page-table-card page-table-card--amber documents-table-card">
        <div className="documents-table-inner">
          <Table
            columns={columns}
            dataSource={documents}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} documents`,
              responsive: true,
            }}
            scroll={{ x: 900 }}
            size={isMobile ? 'small' : 'middle'}
          />
        </div>
      </Card>

      {/* Upload Modal */}
      <Modal
        title="Upload Document"
        wrapClassName="right-aligned-modal"
        zIndex={1200}
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
        width={isMobile ? '95vw' : 520}
      >
        <Form form={form} onFinish={handleUpload} layout="vertical" autoComplete="off">
          <Form.Item
            name="file"
            label="File"
            rules={[{ required: true, message: 'Please select a file' }]}
          >
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls"
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            name="document_type"
            label="Document Type"
            rules={[{ required: true, message: 'Please select document type' }]}
          >
            <Select placeholder="Select document type">
              <Option value="RECEIPT">Receipt</Option>
              <Option value="INVOICE">Invoice</Option>
              <Option value="PO">Purchase Order</Option>
              <Option value="GRPO">Goods Received</Option>
              <Option value="ADJUSTMENT">Adjustment</Option>
              <Option value="TRANSFER">Transfer</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="reference_type"
            label="Reference Type"
            rules={[{ required: true, message: 'Please select reference type' }]}
          >
            <Select placeholder="Select reference type">
              <Option value="SALES_TRANSACTION">Sales Transaction</Option>
              <Option value="PURCHASE_ORDER">Purchase Order</Option>
              <Option value="GOODS_RECEIVED">Goods Received</Option>
              <Option value="STOCK_ADJUSTMENT">Stock Adjustment</Option>
              <Option value="STOCK_TRANSFER">Stock Transfer</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="reference_id"
            label="Reference ID"
            rules={[{ required: true, message: 'Please enter reference ID' }]}
          >
            <Input type="number" placeholder="Enter reference ID" autoComplete="off" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Upload
              </Button>
              <Button onClick={() => setUploadModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        title="Document Preview"
        wrapClassName="right-aligned-modal"
        zIndex={1200}
        centered
        open={previewModalVisible}
        onCancel={closePreview}
        footer={[
          <Button key="close" onClick={closePreview}>Close</Button>,
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={() => selectedDocument && handleDownload(selectedDocument.id, selectedDocument.original_filename)}>Download</Button>,
        ]}
        width={800}
      >
        {selectedDocument && (
          <div>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <strong>File:</strong> {selectedDocument.original_filename}
              </div>
              <div>
                <strong>Type:</strong> <Tag color="blue">{selectedDocument.document_type}</Tag>
              </div>
              <div>
                <strong>Reference:</strong> <Tag color="green">{selectedDocument.reference_type}</Tag> (ID: {selectedDocument.reference_id})
              </div>
              <div>
                <strong>Size:</strong> {formatFileSize(selectedDocument.file_size)}
              </div>
              <div>
                <strong>MIME Type:</strong> {selectedDocument.mime_type}
              </div>
              
              {fileKind(selectedDocument) === 'image' && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  {previewImageLoading ? (
                    <p>Loading preview…</p>
                  ) : previewImageUrl ? (
                    <Image
                      src={previewImageUrl}
                      alt={selectedDocument.original_filename}
                      style={{ maxWidth: '100%', maxHeight: '420px' }}
                    />
                  ) : (
                    <p>{previewError || 'Preview unavailable'}</p>
                  )}
                </div>
              )}

              {fileKind(selectedDocument) === 'pdf' && (
                <div style={{ marginTop: 16 }}>
                  {previewImageLoading ? (
                    <p>Loading preview…</p>
                  ) : previewImageUrl ? (
                    <iframe
                      title={selectedDocument.original_filename}
                      src={previewImageUrl}
                      style={{ width: '100%', height: 480, border: '1px solid #e5e7eb', borderRadius: 8 }}
                    />
                  ) : (
                    <p>{previewError || 'Preview unavailable'}</p>
                  )}
                </div>
              )}

              {fileKind(selectedDocument) === 'other' && (
                <p style={{ marginTop: 16 }}>
                  This file type opens in Download, not in the preview window.
                </p>
              )}
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Documents;